// src/components/Admin/SupportTickets.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Avatar,
  styled,
  LinearProgress,
  Grid,
  alpha,
  Tabs,
  Tab,
  InputAdornment,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from '@mui/material';
import {
  Visibility,
  ConfirmationNumber,
  CheckCircle,
  Cancel,
  HourglassEmpty,
  Search,
  FilterList,
  Send,
  Person,
  Email,
  CalendarToday,
  Message,
  Close,
  Assignment,
} from '@mui/icons-material';
import {
  getTickets,
  getTicket,
  updateTicketStatus,
  assignTicket,
  addTicketResponse,
  resolveTicket,
  closeTicket,
  deleteTicket,
} from '../../services/tickets';
import { getUsers } from '../../services/users';

const teslaColors = {
  primary: '#3E6AE1',
  secondary: '#34C3FF',
  success: '#4CAF50',
  warning: '#ff9800',
  error: '#f44336',
};

const GlassCard = styled(Card)(({ theme }) => ({
  background: theme.palette.mode === 'dark'
    ? 'rgba(30, 30, 30, 0.8)'
    : 'rgba(255, 255, 255, 0.8)',
  backdropFilter: 'blur(10px)',
  borderRadius: '16px',
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.palette.mode === 'dark'
    ? '0 8px 32px 0 rgba(0, 0, 0, 0.36)'
    : '0 8px 32px 0 rgba(0, 0, 0, 0.1)',
}));

const StatsBox = ({ icon, label, value, color }) => (
  <Box
    sx={{
      p: 2,
      borderRadius: '12px',
      background: alpha(color, 0.1),
      display: 'flex',
      alignItems: 'center',
      gap: 2,
    }}
  >
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: '10px',
        background: `linear-gradient(45deg, ${color}, ${alpha(color, 0.7)})`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography variant="h5" fontWeight={700}>
        {value}
      </Typography>
      <Typography variant="body2" color="textSecondary">
        {label}
      </Typography>
    </Box>
  </Box>
);

const getStatusColor = (status) => {
  switch (status) {
    case 'open':
      return teslaColors.error;
    case 'in_progress':
      return teslaColors.primary;
    case 'waiting_response':
      return teslaColors.warning;
    case 'resolved':
      return teslaColors.success;
    case 'closed':
      return 'default';
    default:
      return 'default';
  }
};

const getPriorityColor = (priority) => {
  switch (priority) {
    case 'urgent':
      return teslaColors.error;
    case 'high':
      return teslaColors.warning;
    case 'medium':
      return teslaColors.primary;
    case 'low':
      return 'default';
    default:
      return 'default';
  }
};

function SupportTickets() {
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [responseDialogOpen, setResponseDialogOpen] = useState(false);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [selectedAssignee, setSelectedAssignee] = useState('');
  const [adminUsers, setAdminUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [tabValue, setTabValue] = useState(0);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getTickets({
        status: statusFilter !== 'all' ? statusFilter : undefined,
        priority: priorityFilter !== 'all' ? priorityFilter : undefined,
      });
      setTickets(response.tickets || []);
      setFilteredTickets(response.tickets || []);
    } catch (err) {
      console.error('Error fetching tickets:', err);
      setError('Failed to fetch tickets');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, priorityFilter]);

  const fetchAdminUsers = useCallback(async () => {
    try {
      const response = await getUsers({ role: 'admin' });
      setAdminUsers(response.users || []);
    } catch (err) {
      console.error('Error fetching admin users:', err);
    }
  }, []);

  useEffect(() => {
    fetchTickets();
    fetchAdminUsers();
  }, [fetchTickets, fetchAdminUsers]);

  useEffect(() => {
    let filtered = tickets;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(
        (ticket) =>
          ticket.ticketId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          ticket.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          ticket.submittedBy?.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by tab
    switch (tabValue) {
      case 0: // All
        break;
      case 1: // Open
        filtered = filtered.filter((t) => t.status === 'open');
        break;
      case 2: // In Progress
        filtered = filtered.filter((t) => t.status === 'in_progress');
        break;
      case 3: // Resolved
        filtered = filtered.filter((t) => t.status === 'resolved');
        break;
      default:
        break;
    }

    setFilteredTickets(filtered);
  }, [searchTerm, tabValue, tickets]);

  const handleViewDetails = async (ticket) => {
    try {
      const response = await getTicket(ticket._id);
      setSelectedTicket(response.ticket);
      setDetailsDialogOpen(true);
    } catch (err) {
      setError('Failed to fetch ticket details');
    }
  };

  const handleStatusChange = async (ticketId, newStatus) => {
    try {
      await updateTicketStatus(ticketId, newStatus);
      setSuccess('Ticket status updated successfully');
      fetchTickets();
      if (detailsDialogOpen) {
        const response = await getTicket(ticketId);
        setSelectedTicket(response.ticket);
      }
    } catch (err) {
      setError(err.message || 'Failed to update ticket status');
    }
  };

  const handleAddResponse = async () => {
    if (!responseMessage.trim()) {
      setError('Please enter a response message');
      return;
    }

    try {
      await addTicketResponse(selectedTicket._id, {
        message: responseMessage,
        isInternal: false,
      });
      setSuccess('Response added successfully');
      setResponseMessage('');
      setResponseDialogOpen(false);
      const response = await getTicket(selectedTicket._id);
      setSelectedTicket(response.ticket);
      fetchTickets();
    } catch (err) {
      setError(err.message || 'Failed to add response');
    }
  };

  const handleResolve = async () => {
    if (!resolutionNotes.trim() || resolutionNotes.length < 10) {
      setError('Please provide resolution notes (at least 10 characters)');
      return;
    }

    try {
      await resolveTicket(selectedTicket._id, {
        resolutionNotes: resolutionNotes,
      });
      setSuccess('Ticket resolved successfully');
      setResolveDialogOpen(false);
      setResolutionNotes('');
      setDetailsDialogOpen(false);
      fetchTickets();
    } catch (err) {
      setError(err.message || 'Failed to resolve ticket');
    }
  };

  const handleAssign = async () => {
    if (!selectedAssignee) {
      setError('Please select an assignee');
      return;
    }

    try {
      await assignTicket(selectedTicket._id, selectedAssignee);
      setSuccess('Ticket assigned successfully');
      setAssignDialogOpen(false);
      setSelectedAssignee('');
      const response = await getTicket(selectedTicket._id);
      setSelectedTicket(response.ticket);
      fetchTickets();
    } catch (err) {
      setError(err.message || 'Failed to assign ticket');
    }
  };

  const handleClose = async (ticketId) => {
    try {
      await closeTicket(ticketId);
      setSuccess('Ticket closed successfully');
      fetchTickets();
      if (detailsDialogOpen) {
        setDetailsDialogOpen(false);
      }
    } catch (err) {
      setError(err.message || 'Failed to close ticket');
    }
  };

  const handleDelete = async (ticketId) => {
    if (!window.confirm('Are you sure you want to delete this ticket?')) {
      return;
    }

    try {
      await deleteTicket(ticketId);
      setSuccess('Ticket deleted successfully');
      fetchTickets();
      if (detailsDialogOpen) {
        setDetailsDialogOpen(false);
      }
    } catch (err) {
      setError(err.message || 'Failed to delete ticket');
    }
  };

  const stats = {
    total: tickets.length,
    open: tickets.filter((t) => t.status === 'open').length,
    inProgress: tickets.filter((t) => t.status === 'in_progress').length,
    resolved: tickets.filter((t) => t.status === 'resolved').length,
  };

  return (
    <Box>
      {/* Header */}
      <Box mb={3}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Support Tickets
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Manage and respond to user support requests
        </Typography>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: '12px' }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Stats */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsBox
            icon={<ConfirmationNumber sx={{ color: 'white', fontSize: 24 }} />}
            label="Total Tickets"
            value={stats.total}
            color={teslaColors.primary}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsBox
            icon={<HourglassEmpty sx={{ color: 'white', fontSize: 24 }} />}
            label="Open"
            value={stats.open}
            color={teslaColors.error}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsBox
            icon={<Message sx={{ color: 'white', fontSize: 24 }} />}
            label="In Progress"
            value={stats.inProgress}
            color={teslaColors.warning}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsBox
            icon={<CheckCircle sx={{ color: 'white', fontSize: 24 }} />}
            label="Resolved"
            value={stats.resolved}
            color={teslaColors.success}
          />
        </Grid>
      </Grid>

      {/* Filters */}
      <GlassCard sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by ticket ID, subject, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                  sx: { borderRadius: '12px' },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Priority</InputLabel>
                <Select
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value)}
                  label="Priority"
                  sx={{ borderRadius: '12px' }}
                >
                  <MenuItem value="all">All Priorities</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                  sx={{ borderRadius: '12px' }}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="open">Open</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="waiting_response">Waiting Response</MenuItem>
                  <MenuItem value="resolved">Resolved</MenuItem>
                  <MenuItem value="closed">Closed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </GlassCard>

      {/* Tabs */}
      <GlassCard>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            sx={{ px: 2 }}
          >
            <Tab label={`All (${tickets.length})`} />
            <Tab label={`Open (${stats.open})`} />
            <Tab label={`In Progress (${stats.inProgress})`} />
            <Tab label={`Resolved (${stats.resolved})`} />
          </Tabs>
        </Box>

        <CardContent>
          {loading ? (
            <LinearProgress />
          ) : filteredTickets.length === 0 ? (
            <Box textAlign="center" py={8}>
              <ConfirmationNumber sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="textSecondary" gutterBottom>
                No Tickets Found
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {searchTerm
                  ? 'Try adjusting your search or filters'
                  : 'No support tickets have been submitted yet'}
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Ticket ID</TableCell>
                    <TableCell>Subject</TableCell>
                    <TableCell>Submitted By</TableCell>
                    <TableCell>Priority</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTickets.map((ticket) => (
                    <TableRow key={ticket._id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} color="primary">
                          {ticket.ticketId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {ticket.subject}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {ticket.category}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar sx={{ width: 32, height: 32 }}>
                            {ticket.submittedBy?.firstName?.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2">
                              {ticket.submittedBy?.firstName} {ticket.submittedBy?.lastName}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {ticket.submittedBy?.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={ticket.priority}
                          size="small"
                          sx={{
                            background: alpha(getPriorityColor(ticket.priority), 0.1),
                            color: getPriorityColor(ticket.priority),
                            fontWeight: 600,
                            textTransform: 'uppercase',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={ticket.status.replace('_', ' ')}
                          size="small"
                          sx={{
                            background: alpha(getStatusColor(ticket.status), 0.1),
                            color: getStatusColor(ticket.status),
                            fontWeight: 600,
                            textTransform: 'capitalize',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {ticket.createdAt
                          ? new Date(ticket.createdAt).toLocaleDateString()
                          : 'N/A'}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => handleViewDetails(ticket)}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </GlassCard>

      {/* Ticket Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: { xs: '95vw', sm: '80vw', md: '840px' },
            maxWidth: '860px !important',
            maxHeight: '88vh',
            borderRadius: '14px',
            background: (theme) =>
              theme.palette.mode === 'dark'
                ? 'rgba(30, 30, 30, 0.95)'
                : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
          },
        }}
      >
        <DialogTitle sx={{ px: 2.5, py: 1.25 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.05rem' }}>
              Ticket Details
            </Typography>
            <IconButton onClick={() => setDetailsDialogOpen(false)} size="small">
              <Close sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ px: 2.5, py: 1.75 }}>
          {selectedTicket && (
            <Box>
              {/* Ticket Header */}
              <Box mb={3}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Typography variant="h6" fontWeight={600}>
                    {selectedTicket.ticketId}
                  </Typography>
                  <Box display="flex" gap={1}>
                    <Chip
                      label={selectedTicket.priority}
                      size="small"
                      sx={{
                        background: alpha(getPriorityColor(selectedTicket.priority), 0.1),
                        color: getPriorityColor(selectedTicket.priority),
                        fontWeight: 600,
                        textTransform: 'uppercase',
                      }}
                    />
                    <Chip
                      label={selectedTicket.status.replace('_', ' ')}
                      size="small"
                      sx={{
                        background: alpha(getStatusColor(selectedTicket.status), 0.1),
                        color: getStatusColor(selectedTicket.status),
                        fontWeight: 600,
                        textTransform: 'capitalize',
                      }}
                    />
                  </Box>
                </Box>

                <Typography variant="h6" gutterBottom>
                  {selectedTicket.subject}
                </Typography>

                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Person sx={{ color: teslaColors.primary }} />
                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          Submitted By
                        </Typography>
                        <Typography variant="body2">
                          {selectedTicket.submittedBy?.firstName}{' '}
                          {selectedTicket.submittedBy?.lastName}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {selectedTicket.submittedBy?.email}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <CalendarToday sx={{ color: teslaColors.primary }} />
                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          Created
                        </Typography>
                        <Typography variant="body2">
                          {selectedTicket.createdAt
                            ? new Date(selectedTicket.createdAt).toLocaleString()
                            : 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  {selectedTicket.assignedTo && (
                    <Grid item xs={12} sm={6}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Assignment sx={{ color: teslaColors.primary }} />
                        <Box>
                          <Typography variant="caption" color="textSecondary">
                            Assigned To
                          </Typography>
                          <Typography variant="body2">
                            {selectedTicket.assignedTo?.firstName}{' '}
                            {selectedTicket.assignedTo?.lastName}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}

                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Message sx={{ color: teslaColors.primary }} />
                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          Category
                        </Typography>
                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                          {selectedTicket.category?.replace('_', ' ')}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Description */}
              <Box mb={3}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Description
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'pre-wrap' }}>
                  {selectedTicket.description}
                </Typography>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Responses */}
              <Box>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Responses ({selectedTicket.responses?.length || 0})
                </Typography>
                {selectedTicket.responses && selectedTicket.responses.length > 0 ? (
                  <List>
                    {selectedTicket.responses.map((response, index) => (
                      <React.Fragment key={index}>
                        <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                          <ListItemAvatar>
                            <Avatar>
                              {response.respondedBy?.firstName?.charAt(0)}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="body2" fontWeight={600}>
                                  {response.respondedBy?.firstName}{' '}
                                  {response.respondedBy?.lastName}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {response.respondedAt
                                    ? new Date(response.respondedAt).toLocaleString()
                                    : 'N/A'}
                                </Typography>
                              </Box>
                            }
                            secondary={
                              <Typography
                                variant="body2"
                                color="textSecondary"
                                sx={{ mt: 1, whiteSpace: 'pre-wrap' }}
                              >
                                {response.message}
                              </Typography>
                            }
                          />
                        </ListItem>
                        {index < selectedTicket.responses.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="textSecondary" sx={{ py: 2 }}>
                    No responses yet
                  </Typography>
                )}
              </Box>

              {/* Resolution */}
              {selectedTicket.resolution && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      Resolution
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedTicket.resolution.resolutionNotes}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                      Resolved by {selectedTicket.resolution.resolvedBy?.firstName}{' '}
                      {selectedTicket.resolution.resolvedBy?.lastName} on{' '}
                      {selectedTicket.resolution.resolvedAt
                        ? new Date(selectedTicket.resolution.resolvedAt).toLocaleString()
                        : 'N/A'}
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.25, gap: 1 }}>
          <Button
            onClick={() => handleDelete(selectedTicket._id)}
            color="error"
            size="small"
            sx={{ borderRadius: '8px', textTransform: 'none' }}
          >
            Delete
          </Button>
          <Box sx={{ flex: 1 }} />
          {selectedTicket?.status !== 'closed' && (
            <>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setAssignDialogOpen(true)}
                sx={{ borderRadius: '8px', textTransform: 'none' }}
              >
                Assign
              </Button>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <Select
                  value={selectedTicket?.status || ''}
                  onChange={(e) => handleStatusChange(selectedTicket._id, e.target.value)}
                  sx={{ borderRadius: '8px' }}
                >
                  <MenuItem value="open">Open</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="waiting_response">Waiting Response</MenuItem>
                  <MenuItem value="resolved">Resolved</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setResponseDialogOpen(true)}
                sx={{ borderRadius: '8px', textTransform: 'none' }}
              >
                Add Response
              </Button>
              {selectedTicket?.status !== 'resolved' && (
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  onClick={() => setResolveDialogOpen(true)}
                  sx={{ borderRadius: '8px', textTransform: 'none' }}
                >
                  Resolve
                </Button>
              )}
              <Button
                variant="outlined"
                color="warning"
                size="small"
                onClick={() => handleClose(selectedTicket._id)}
                sx={{ borderRadius: '8px', textTransform: 'none' }}
              >
                Close
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Add Response Dialog */}
      <Dialog
        open={responseDialogOpen}
        onClose={() => setResponseDialogOpen(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: { xs: '95vw', sm: '520px' },
            maxWidth: '540px !important',
            maxHeight: '85vh',
            borderRadius: '14px',
            background: (theme) =>
              theme.palette.mode === 'dark'
                ? 'rgba(30, 30, 30, 0.95)'
                : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
          },
        }}
      >
        <DialogTitle sx={{ px: 2.5, py: 1.25 }}>
          <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.05rem' }}>
            Add Response
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ px: 2.5, py: 1.5 }}>
          <TextField
            fullWidth
            multiline
            rows={3.5}
            size="small"
            label="Response Message"
            value={responseMessage}
            onChange={(e) => setResponseMessage(e.target.value)}
            placeholder="Type your response here..."
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.25 }}>
          <Button onClick={() => setResponseDialogOpen(false)} size="small" sx={{ borderRadius: '8px', textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<Send sx={{ fontSize: 16 }} />}
            onClick={handleAddResponse}
            disabled={!responseMessage.trim()}
            sx={{ borderRadius: '8px', textTransform: 'none', px: 2.5 }}
          >
            Send Response
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resolve Dialog */}
      <Dialog
        open={resolveDialogOpen}
        onClose={() => setResolveDialogOpen(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: { xs: '95vw', sm: '520px' },
            maxWidth: '540px !important',
            maxHeight: '85vh',
            borderRadius: '14px',
            background: (theme) =>
              theme.palette.mode === 'dark'
                ? 'rgba(30, 30, 30, 0.95)'
                : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
          },
        }}
      >
        <DialogTitle sx={{ px: 2.5, py: 1.25 }}>
          <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.05rem' }}>
            Resolve Ticket
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ px: 2.5, py: 1.5 }}>
          <Typography variant="body2" color="textSecondary" mb={1.5} sx={{ fontSize: '0.82rem' }}>
            Please provide resolution notes for this ticket. This will be visible to the user.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            size="small"
            label="Resolution Notes"
            value={resolutionNotes}
            onChange={(e) => setResolutionNotes(e.target.value)}
            placeholder="Describe how the issue was resolved..."
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            helperText={`${resolutionNotes.length} / 1000 characters (minimum 10 required)`}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.25 }}>
          <Button onClick={() => setResolveDialogOpen(false)} size="small" sx={{ borderRadius: '8px', textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            size="small"
            onClick={handleResolve}
            disabled={resolutionNotes.length < 10}
            sx={{ borderRadius: '8px', textTransform: 'none', px: 2.5 }}
          >
            Confirm Resolution
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Dialog */}
      <Dialog
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: { xs: '95vw', sm: '460px' },
            maxWidth: '480px !important',
            maxHeight: '85vh',
            borderRadius: '14px',
            background: (theme) =>
              theme.palette.mode === 'dark'
                ? 'rgba(30, 30, 30, 0.95)'
                : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
          },
        }}
      >
        <DialogTitle sx={{ px: 2.5, py: 1.25 }}>
          <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.05rem' }}>
            Assign Ticket
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ px: 2.5, py: 1.5 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Assign To</InputLabel>
            <Select
              value={selectedAssignee}
              onChange={(e) => setSelectedAssignee(e.target.value)}
              label="Assign To"
              sx={{ borderRadius: '8px' }}
            >
              {adminUsers.map((admin) => (
                <MenuItem key={admin._id} value={admin._id}>
                  {admin.firstName} {admin.lastName} ({admin.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.25 }}>
          <Button onClick={() => setAssignDialogOpen(false)} size="small" sx={{ borderRadius: '8px', textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleAssign}
            disabled={!selectedAssignee}
            sx={{ borderRadius: '8px', textTransform: 'none', px: 2.5 }}
          >
            Assign
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SupportTickets;